
import React from "react";
import { Activity } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { athleteTierSchema } from "@/contexts/program-creation/types";

interface TrainingAssessmentSectionProps {
  form: UseFormReturn<z.infer<typeof athleteTierSchema>>;
}

export default function TrainingAssessmentSection({ form }: TrainingAssessmentSectionProps) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-white/90 border-b border-white/20 pb-2">
        <Activity size={18} />
        <h4 className="font-medium">Training Assessment</h4>
      </div>
      
      <FormField
        control={form.control}
        name="sportTrainingLoad"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white/90 text-sm">
              How often do they practice or compete in their sport each week?
            </FormLabel>
            <FormControl>
              <Input 
                placeholder="e.g., Team practice 3x/week, game every Sunday" 
                className="bg-white/20 border-white/30 text-white w-full h-12"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="sportInjuries"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white/90 text-sm">
              Any injuries from their sport or recurring issues we should plan around?
            </FormLabel>
            <FormControl>
              <Textarea 
                placeholder="e.g., Left ankle sprain that flares up occasionally" 
                className="bg-white/20 border-white/30 text-white min-h-[100px]"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="recoveryTravel"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white/90 text-sm">
              Do they have long travel, tournaments, or other stressors during the season that affect training availability?
            </FormLabel>
            <FormControl>
              <Textarea 
                placeholder="e.g., Travel for games every other week" 
                className="bg-white/20 border-white/30 text-white min-h-[100px]"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
